import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import convertDataAttributes from '@widgets/shared/convertDataAttributes';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextInput from '@widgets/shared/components/form-controls/TextInput';
import { accountFormSchema } from '@widgets/AccountForm/validation/schema';
import FormField from '@enquiryForm/components/FormField';
import { FieldType } from '@enquiryForm/types/config';
import ToggleInput from '@widgets/shared/components/form-controls/ToggleInput';
import AddressInput from '@widgets/shared/components/form-controls/AddressInput';
import { useRef } from 'preact/compat';
import API from '@widgets/shared/API/connection';
import FormNotices from '@widgets/shared/components/FormNotices';
import { useCallback, useMemo, useState } from 'react';
import { successUrlRedirect } from '@widgets/shared/registerOrLoginAction';
import useDidMount from 'beautiful-react-hooks/useDidMount';
function Form(props) {
    const ref = useRef(null);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [isRequesting, setIsRequesting] = useState(false);
    const [successMessage, setSuccessMessage] = useState(undefined);
    const handleErrorClose = useCallback(() => {
        setErrorMessage(undefined);
    }, []);
    const handleSuccessClose = useCallback(() => setSuccessMessage(undefined), []);
    const { action, email, firstName, surname, mobile, dateOfBirth, addressLine1, addressLine2, addressTown, addressPostcode, addressCountry, marketingPreferences, successUrl: successUrlOverride, marketingPreferenceText, } = props;
    const form = useForm({
        mode: 'onChange',
        resolver: yupResolver(accountFormSchema),
        defaultValues: {
            firstName,
            surname,
            email,
            mobile,
            dateOfBirth: dateOfBirth ? new Date(dateOfBirth) : new Date(Date.now()),
            addressLine1,
            addressLine2,
            addressTown,
            addressPostcode,
            addressCountry,
            marketingPreferences: marketingPreferences ?? false,
        },
    });
    const { formState: { isValid, errors }, handleSubmit, setError, getValues, trigger, } = form;
    const handleMount = useCallback(() => {
        trigger(undefined, { shouldFocus: true })
            .then((res) => { });
    }, [trigger]);
    useDidMount(handleMount);
    const scrollToTop = useCallback(() => {
        if (ref.current) {
            ref.current.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        }
    }, [ref]);
    const handleSubmitEvent = () => {
        setIsRequesting(true);
        const values = getValues();
        const formEl = ref.current;
        API.account(values).then((res) => {
            if (res.success) {
                const { successUrl: serverSuccessUrl, message: serverSuccessMessage } = res;
                if (serverSuccessMessage) {
                    setSuccessMessage(serverSuccessMessage);
                    if (!serverSuccessUrl && !successUrlOverride) {
                        scrollToTop();
                    }
                }
                if (serverSuccessUrl || successUrlOverride) {
                    successUrlRedirect(successUrlOverride, res.successUrl);
                }
            }
            else {
                res.errors.forEach((error) => {
                    if (error.field) {
                        console.log(`Setting ${error.field} to ${error.message}`);
                        setError(error.field, { type: 'custom', message: error.message }, { shouldFocus: false });
                    }
                });
                if (res.message) {
                    setErrorMessage(res.message);
                    scrollToTop();
                }
            }
        })
            .finally(() => {
            setIsRequesting(false);
        });
    };
    const formInvalid = useMemo(() => !isValid || isRequesting, [isValid, isRequesting]);
    return (_jsx(FormProvider, { ...form, children: _jsxs("form", { ref: ref, method: "POST", noValidate: true, onSubmit: handleSubmit(handleSubmitEvent), children: [errorMessage && (_jsx("div", { style: { marginBottom: '1rem' }, children: _jsx(FormNotices, { message: errorMessage, type: "error", handleClose: handleErrorClose }) })), successMessage && (_jsx("div", { style: { marginBottom: '1rem' }, children: _jsx(FormNotices, { message: successMessage, type: "success", handleClose: handleSuccessClose }) })), _jsxs("div", { className: "form__section", children: [_jsx(TextInput, { inputAttrs: { id: 'first-name', name: 'firstName', type: 'text' }, labelAttrs: {
                                htmlFor: 'first-name',
                                text: 'First Name',
                                isFieldRequired: true,
                            }, forceError: true }), _jsx(TextInput, { inputAttrs: { id: 'surname', name: 'surname', type: 'text' }, labelAttrs: {
                                htmlFor: 'surname',
                                text: 'Surname',
                                isFieldRequired: true,
                            }, forceError: true }), _jsx(TextInput, { inputAttrs: {
                                id: 'email',
                                name: 'email',
                                type: 'email',
                            }, labelAttrs: {
                                htmlFor: 'email',
                                text: 'Email',
                                isFieldRequired: true,
                            }, forceError: true }), _jsx(FormField, { id: "mobile", name: "mobile", label: "Mobile Number", required: true, type: FieldType.PhoneInput, defaultValue: "", placeholder: "", content: "", forceError: true }), _jsx(FormField, { id: "date-of-birth", name: "dateOfBirth", label: "Date of Birth", required: true, type: FieldType.DatepickerInput, content: "", forceError: true }), _jsx(AddressInput, { addressLineOneName: "addressLine1", addressLineTwoName: "addressLine2", 
                            // addressLineThreeName="addressLine3"
                            addressTownName: "addressTown", addressPostcodeName: "addressPostcode", addressCountry: "addressCountry", forceOpen: true, forceError: true, inputAttrs: {
                                name: 'address-search',
                                id: 'address-search',
                            }, labelAttrs: {
                                isFieldRequired: false,
                                text: 'Search for an address',
                            } })] }), _jsxs("div", { className: "form__section", children: [_jsx("h3", { children: "Marketing Preferences" }), _jsx(ToggleInput, { inputAttrs: {
                                id: 'marketing-preferences',
                                name: 'marketingPreferences',
                            }, labelAttrs: {
                                htmlFor: 'marketing-preferences',
                                text: marketingPreferenceText,
                                isFieldRequired: false,
                            } })] }), _jsx("div", { className: "form__actions", children: _jsx("button", { type: "submit", className: "btn", disabled: formInvalid, children: "Save Changes" }) })] }) }));
}
export default function AccountForm(props) {
    const { dataset } = props;
    const formConfig = convertDataAttributes(dataset ?? {});
    return (_jsx(Form, { ...formConfig }));
}
